import type { WordReplacementMap } from "./word-replacement";

const STORAGE_KEY = "voxdoc-replacements";
const LEGACY_KEY = "word-replacements";

interface ReplacementStore {
  version: number;
  defaults: WordReplacementMap;
  custom: WordReplacementMap;
  deletedDefaults: string[]; // default keys the user removed
}

function getStore(): ReplacementStore | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

function saveStore(store: ReplacementStore) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

/** Get the merged replacement map (defaults + custom, minus deleted defaults) */
export function getMergedMap(store: ReplacementStore): WordReplacementMap {
  const merged: WordReplacementMap = {};
  for (const [k, v] of Object.entries(store.defaults)) {
    if (!store.deletedDefaults.includes(k)) {
      merged[k] = v;
    }
  }
  // Custom overrides defaults
  for (const [k, v] of Object.entries(store.custom)) {
    merged[k] = v;
  }
  return merged;
}

/** Migrate from old flat localStorage format */
function migrateFromLegacy(
  legacyMap: WordReplacementMap,
  serverDefaults: WordReplacementMap,
  serverVersion: number
): ReplacementStore {
  // Figure out which entries are user-custom (not in server defaults)
  const custom: WordReplacementMap = {};
  for (const [k, v] of Object.entries(legacyMap)) {
    if (!(k in serverDefaults) || serverDefaults[k] !== v) {
      custom[k] = v;
    }
  }

  // Figure out which server defaults the user deleted
  const deletedDefaults: string[] = [];
  for (const k of Object.keys(serverDefaults)) {
    if (!(k in legacyMap) && !(k in custom)) {
      deletedDefaults.push(k);
    }
  }

  const store: ReplacementStore = {
    version: serverVersion,
    defaults: serverDefaults,
    custom,
    deletedDefaults,
  };

  // Clean up legacy key
  localStorage.removeItem(LEGACY_KEY);
  saveStore(store);
  return store;
}

/** Load replacements, merging server updates and preserving user customizations */
export async function loadReplacements(): Promise<{
  map: WordReplacementMap;
  store: ReplacementStore;
}> {
  // Fetch server defaults
  const res = await fetch("/word-replacements.json");
  const serverData = await res.json();
  const serverVersion: number = serverData._version || 0;
  const serverDefaults: WordReplacementMap = {};
  for (const [k, v] of Object.entries(serverData)) {
    if (k !== "_version") serverDefaults[k] = v as string;
  }

  // Check for legacy format first
  const legacyRaw = localStorage.getItem(LEGACY_KEY);
  if (legacyRaw && !localStorage.getItem(STORAGE_KEY)) {
    try {
      const legacyMap = JSON.parse(legacyRaw);
      const store = migrateFromLegacy(legacyMap, serverDefaults, serverVersion);
      return { map: getMergedMap(store), store };
    } catch {
      // Corrupt legacy data, start fresh
    }
  }

  const existing = getStore();

  if (!existing) {
    // First-time user: server defaults are the full library
    const store: ReplacementStore = {
      version: serverVersion,
      defaults: serverDefaults,
      custom: {},
      deletedDefaults: [],
    };
    saveStore(store);
    return { map: getMergedMap(store), store };
  }

  if (existing.version < serverVersion) {
    // Server has new version: merge new defaults in
    const newDefaults = { ...existing.defaults };
    for (const [k, v] of Object.entries(serverDefaults)) {
      // Only add terms that are genuinely new (not previously deleted by user)
      if (!(k in existing.defaults) && !existing.deletedDefaults.includes(k)) {
        newDefaults[k] = v;
      }
      // Update existing default values if they changed
      if (k in existing.defaults && existing.defaults[k] !== v) {
        newDefaults[k] = v;
      }
    }

    const store: ReplacementStore = {
      version: serverVersion,
      defaults: newDefaults,
      custom: existing.custom,
      deletedDefaults: existing.deletedDefaults,
    };
    saveStore(store);
    return { map: getMergedMap(store), store };
  }

  // Same version, no merge needed
  return { map: getMergedMap(existing), store: existing };
}

/** Add a user custom replacement */
export function addReplacement(pattern: string, replacement: string): WordReplacementMap {
  const store = getStore();
  if (!store) return {};

  store.custom[pattern.toLowerCase()] = replacement;
  // If this was a deleted default, un-delete it
  store.deletedDefaults = store.deletedDefaults.filter((k) => k !== pattern.toLowerCase());
  saveStore(store);
  return getMergedMap(store);
}

/** Delete a replacement (track if it's a default) */
export function deleteReplacement(pattern: string): WordReplacementMap {
  const store = getStore();
  if (!store) return {};

  const key = pattern.toLowerCase();

  // If it's a custom term, just remove it
  if (key in store.custom) {
    delete store.custom[key];
  }

  // If it's a default term, track the deletion so it doesn't come back
  if (key in store.defaults) {
    if (!store.deletedDefaults.includes(key)) {
      store.deletedDefaults.push(key);
    }
  }

  saveStore(store);
  return getMergedMap(store);
}

/** Reset to server defaults (clears custom + deletions) */
export async function resetToDefaults(): Promise<WordReplacementMap> {
  const res = await fetch("/word-replacements.json");
  const serverData = await res.json();
  const serverVersion: number = serverData._version || 0;
  const serverDefaults: WordReplacementMap = {};
  for (const [k, v] of Object.entries(serverData)) {
    if (k !== "_version") serverDefaults[k] = v as string;
  }

  const store: ReplacementStore = {
    version: serverVersion,
    defaults: serverDefaults,
    custom: {},
    deletedDefaults: [],
  };
  saveStore(store);
  localStorage.removeItem(LEGACY_KEY);
  return getMergedMap(store);
}

/** Get total count of active replacements */
export function getReplacementCount(store: ReplacementStore): number {
  return Object.keys(getMergedMap(store)).length;
}
